import {
  getArticleCategoryNames,
  getKnowledgeCategories,
  getKnowledgeCategoryLink,
  getKnowledgeIndexLink,
} from '../../utils/knowledge/knowledgeArticles';
import { resolveKnowledgeLocale } from '../../utils/knowledge/knowledgeCopy';
import { sanitizeKnowledgeHtml } from '../../utils/knowledge/sanitizeKnowledgeHtml';
import { KnowledgeArticle as KnowledgeArticleType, KnowledgeChannel } from '../../utils/knowledge/types';
import {
  KnowledgeArticleBody,
  KnowledgeArticleDetailTitle,
  KnowledgeBreadcrumbCurrent,
  KnowledgeBreadcrumbLink,
  KnowledgeBreadcrumbNav,
  KnowledgeBreadcrumbSep,
  KnowledgeMainColumn,
  KnowledgePageLayout,
} from './KnowledgeCentre.styles';
import { KnowledgeHelpSidebar } from './KnowledgeHelpSidebar';

interface KnowledgeArticleProps {
  article: KnowledgeArticleType;
  locale: string;
  channel: KnowledgeChannel;
  topArticles: { urlName: string; title: string }[];
}

export const KnowledgeArticle = ({
  article,
  locale,
  channel,
  topArticles,
}: KnowledgeArticleProps) => {
  const resolvedLocale = resolveKnowledgeLocale(locale);
  const bodyHtml = sanitizeKnowledgeHtml(article.bodyHtml, resolvedLocale, channel);
  const channelProp = { $channel: channel };
  const indexHref = getKnowledgeIndexLink(resolvedLocale, channel);
  const categoryNames = getArticleCategoryNames(article.urlName, resolvedLocale, channel);
  const primaryCategory = getPrimaryCategoryForArticle(article.urlName, resolvedLocale, channel);

  return (
    <KnowledgePageLayout {...channelProp}>
      <KnowledgeMainColumn>
        <KnowledgeBreadcrumbNav>
          <KnowledgeBreadcrumbLink href={indexHref}>Home</KnowledgeBreadcrumbLink>
          {primaryCategory ? (
            <>
              <KnowledgeBreadcrumbSep>›</KnowledgeBreadcrumbSep>
              <KnowledgeBreadcrumbLink
                href={getKnowledgeCategoryLink(resolvedLocale, channel, primaryCategory.slug)}
              >
                {primaryCategory.name}
              </KnowledgeBreadcrumbLink>
            </>
          ) : categoryNames[0] ? (
            <>
              <KnowledgeBreadcrumbSep>›</KnowledgeBreadcrumbSep>
              <KnowledgeBreadcrumbCurrent>{categoryNames[0]}</KnowledgeBreadcrumbCurrent>
            </>
          ) : null}
          <KnowledgeBreadcrumbSep>›</KnowledgeBreadcrumbSep>
          <KnowledgeBreadcrumbCurrent>{article.title}</KnowledgeBreadcrumbCurrent>
        </KnowledgeBreadcrumbNav>
        <KnowledgeArticleDetailTitle>{article.title}</KnowledgeArticleDetailTitle>
        {bodyHtml ? (
          <KnowledgeArticleBody dangerouslySetInnerHTML={{ __html: bodyHtml }} />
        ) : null}
      </KnowledgeMainColumn>
      <KnowledgeHelpSidebar
        channel={channel}
        locale={resolvedLocale}
        topArticles={topArticles}
      />
    </KnowledgePageLayout>
  );
};

function getPrimaryCategoryForArticle(
  articleUrlName: string,
  locale: ReturnType<typeof resolveKnowledgeLocale>,
  channel: KnowledgeChannel
) {
  return getKnowledgeCategories(locale, channel).find((category) =>
    category.articleSlugs.some(
      (slug) => slug === articleUrlName || slug.toLowerCase() === articleUrlName.toLowerCase()
    )
  );
}
